import { useEffect, useLayoutEffect } from 'react';
import { toast } from 'react-toastify';
import { errorMsg, userListToLabelEmailPhoneValue } from '../../../../../../../Apis/functions';
import {
    GetAttendanceShiftById,
    UpdateAttendanceShift
} from '../../../../../../../reduxToolkit/AttendanceShift/AttendanceShiftApi';
import { MODEL_DATA, STEPS_UPDATE_SHIFT_ACCESS } from '../Model/Models';
import { PersonChipBlack } from "../../../../../../../components/buttons/IconButtons";
import CommonShiftAccessApp from './CommonShiftAccessApp';
import { permissionObj } from '../../../../../../../Helpers/permission';

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Update-only hook. Loads the shift detail into the local selection lists,
 * builds the update stepper labels and resubmits the single shift body.
 * Mirrors Devices UpdateDevicesApp.
 */
export const EditShiftAccessApp = () => {

    const {
        DEV,
        permission,
        emptyFields,
        disabled,
        handleChange,
        selectDeleteDevices, setSelectDeleteDevices,
        selectDeleteDepartments, setSelectDeleteDepartments,
        selectDeleteUser, setSelectDeleteUser,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        handleOnBack,
        id,
        t,
        dispatch,
        item,
        setItem,
        validate,
        setSteps,
        setDevices,
        setUser,
        setDepartments,
        devices,
        departments,
        user,
        searchElement,
        setSearchElement,
        modified, setModified
    } = CommonShiftAccessApp();


    useLayoutEffect(() => {
        const stepArray = Object.values(STEPS_UPDATE_SHIFT_ACCESS)
            .filter(step => step?.TRANS && typeof step.INDEX === "number")
            .sort((a, b) => a.INDEX - b.INDEX)
            .map(step => t(step.TRANS));
        setSteps(stepArray);
    }, [t]);

    useEffect(() => {
        if (id) getData();
    }, [id]);

    useEffect(() => {
        if (modified && id) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Flags server-loaded items as already assigned. They must stay in the
     * section list (toRegister source + assigned-tab delete), but the Add tab
     * must show only the newly added ones, so it filters this flag out.
     * @param {Array<Object>} list - Mapped section items.
     * @returns {Array<Object>}
     */
    const markAssigned = (list) => (list || []).map(e => ({ ...e, isAssigned: true }));

    /**
     * Loads the shift detail by id and hydrates the form and the section
     * selection lists (devices / users / departments), flagging each as assigned.
     */
    const getData = () => {
        dispatch(GetAttendanceShiftById(id)).then(({ payload: { data: { data } } }) => {
            setItem({ ...MODEL_DATA, id: data.id, name: data.name });
            setDevices(markAssigned(userListToLabelEmailPhoneValue(data.devices, 'id', false, null)));
            setUser(markAssigned(userListToLabelEmailPhoneValue(data.users, 'userId', true, PersonChipBlack)));
            setDepartments(markAssigned(userListToLabelEmailPhoneValue(data.departments, 'id', false, null)));
        });
    }

    /**
     * Validates, builds the shift body and updates it; on success notifies and
     * triggers a reload of the shift detail.
     */
    const handleAddEdit = () => {
        if (!validate()) return;
        const body = item.toRegister(devices, departments, user);
        dispatch(UpdateAttendanceShift(body)).then(({ payload: { data: { success, data, code } } }) => {
            if (success && data?.id) {
                toast.success(t('has_been_updated_successfully'));
                setModified(true);
            } else errorMsg(code);
        });
    }

    // Read only users reach this same view as details, so every section is view-only
    const isDisabled = disabled || !(DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_SHIFT_UPDATE));

    return {
        modified,
        setModified,
        id,
        DEV,
        permission,
        emptyFields,
        disabled: isDisabled,
        item,
        handleChange,

        devices, setDevices,
        selectDeleteDevices, setSelectDeleteDevices,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,
        searchElement,
        setSearchElement,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        handleAddEdit,
        handleOnBack,
    }
}
